/**
 * External run execution: identify → this run → accept → collapsible list.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { applyRunAction, isRunSettled, RUN_ACTIONS } from '@x9/routine-run-model';
import type { RunAction, RunDoc, RunStep } from '@x9/routine-run-model';
import AppInstallHeadBanner from '@/components/AppInstallHeadBanner';
import { useAppInstallBanner } from '@/hooks/useAppInstallBanner';
import { getFirebaseAuth, getFirebaseDb } from '@/config/firebase';
import { ensureSessionReady, hasVerifiedPhoneOnSession, signOutOfSession } from '@/services/auth';
import { ensureContactForPhoneAuth } from '../services/api';
import { classifyCallableError, classifyRunActionError } from '../services/errors';
import {
  advanceRunStepFromWeb,
  newActionId,
  resolveRunForWeb,
} from '../services/runApi';
import {
  clearPendingAction,
  loadPendingAction,
  savePendingAction,
} from '../services/runPending';
import LoadingStep from '../steps/LoadingStep';
import PhoneVerifyStep from '../steps/PhoneVerifyStep';
import RunListView from '../steps/RunListView';
import RunStepSheet from '../steps/RunStepSheet';
import RunEvidenceModal from '../steps/RunEvidenceModal';
import RunEditNoteModal from '../steps/RunEditNoteModal';
import RunDoneStep from '../steps/RunDoneStep';
import ErrorScreen from '../components/ErrorScreen';
import X9Logo from '../components/X9Logo';
import { homeOutline } from 'ionicons/icons';
import FloatingFooter, {
  FloatingFooterCta,
  FloatingFooterNavButton,
} from '../components/FloatingFooter';
import { HOME_PATH, useHasHome } from '@/components/useHasHome';
import { goBackInApp, inAppBackTarget, navigateTo } from '@/navigation';
import { isStepResolved, stepNeedsEvidence, stepNoteRequiredForCurrent } from '../runEvidence';
import type { PendingRunAction, WebRun, WebRunStep } from '../runTypes';

type Phase = 'loading' | 'phone_verify' | 'ready' | 'done' | 'error';

interface Props {
  shortId: string;
}

function toRunDoc(run: WebRun): RunDoc {
  return {
    runId: run.runId,
    schemaVersion: 2,
    ownerUserId: run.ownerUserId,
    executorUserId: run.executorUserId,
    state: run.state as RunDoc['state'],
    acceptedAt: run.acceptedAt,
    startedAt: run.startedAt,
    completedAt: run.completedAt,
    sectionsSnapshot: run.sectionsSnapshot,
    steps: run.steps.map((s) => ({
      ...s,
      status: s.status as RunStep['status'],
    })),
  };
}

function actionForStep(
  step: WebRunStep,
  input: { outcome: 'yes' | 'no'; details?: string; proofUrls?: string[] },
): RunAction {
  if (step.resolutionMode === 'execute') {
    return input.outcome === 'yes'
      ? {
          type: RUN_ACTIONS.COMPLETE_STEP,
          stepId: step.stepId,
          note: input.details,
          proofUrls: input.proofUrls,
        }
      : {
          type: RUN_ACTIONS.DENY_STEP,
          stepId: step.stepId,
          note: input.details,
        };
  }
  return {
    type: RUN_ACTIONS.CHECK_STEP,
    stepId: step.stepId,
    outcome: input.outcome,
    details: input.details,
    proofUrls: input.proofUrls,
  };
}

function mergeRun(prev: WebRun, next: RunDoc): WebRun {
  const byId = new Map(prev.steps.map((s) => [s.stepId, s]));
  return {
    ...prev,
    state: next.state || prev.state,
    acceptedAt: next.acceptedAt ?? prev.acceptedAt,
    startedAt: next.startedAt ?? prev.startedAt,
    completedAt: next.completedAt ?? prev.completedAt,
    executorUserId: next.executorUserId ?? prev.executorUserId,
    steps: (next.steps || []).map((s) => {
      const prior = byId.get(s.stepId || '');
      return {
        stepId: s.stepId,
        order: s.order ?? prior?.order ?? 0,
        title: s.title || prior?.title || '',
        description: (s.description as string | null) ?? prior?.description ?? null,
        sectionId: s.sectionId ?? prior?.sectionId ?? null,
        sectionName: s.sectionName ?? prior?.sectionName ?? null,
        status: s.status || prior?.status || 'pending',
        outcome: (s.outcome as WebRunStep['outcome']) ?? prior?.outcome ?? null,
        points: s.points ?? prior?.points ?? 1,
        resolutionMode: s.resolutionMode === 'execute' ? 'execute' : 'check',
        requireProof: s.requireProof !== false,
        checkRequireNote: s.checkRequireNote !== false,
        checkRequirePhoto: s.checkRequirePhoto !== false,
        checkYesRequireNote: s.checkYesRequireNote === true,
        checkYesRequirePhoto: s.checkYesRequirePhoto === true,
        photoUrl: prior?.photoUrl ?? null,
        proofUrls: (s.proofUrls as string[] | undefined) || prior?.proofUrls || [],
        note: (s.note as string | null) ?? prior?.note ?? null,
        details: (s.details as string | null) ?? prior?.details ?? null,
        completedAt: s.completedAt ?? prior?.completedAt ?? null,
        resolvedBy: (s.resolvedBy as string | null) ?? prior?.resolvedBy ?? null,
      };
    }),
  };
}

export default function RunPage({ shortId }: Props) {
  const installBanner = useAppInstallBanner({ softShowOnMount: true });
  const [phase, setPhase] = useState<Phase>('loading');
  // Keyed on phase: an executor who verifies their number mid-run earns a home
  // they did not have when this page mounted.
  const hasHome = useHasHome(phase);
  const [run, setRun] = useState<WebRun | null>(null);
  const [errorReason, setErrorReason] = useState('');
  const [pending, setPending] = useState<PendingRunAction | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [openStep, setOpenStep] = useState<WebRunStep | null>(null);
  const [evidence, setEvidence] = useState<{
    step: WebRunStep;
    outcome: 'yes' | 'no';
  } | null>(null);
  const [editingNote, setEditingNote] = useState(false);
  const [sending, setSending] = useState(false);
  const [forcePhoneInput, setForcePhoneInput] = useState(false);
  const sendingRef = useRef(false);
  const liveRef = useRef(false);
  const restoredRef = useRef(false);
  // Mirrors of the two states the send pipeline reads mid-callback, where the
  // React values are still a render behind.
  const runRef = useRef<WebRun | null>(null);
  const pendingRef = useRef<PendingRunAction | null>(null);

  const commitRun = useCallback((next: WebRun) => {
    runRef.current = next;
    setRun(next);
  }, []);

  const commitPending = useCallback((next: PendingRunAction | null) => {
    pendingRef.current = next;
    setPending(next);
  }, []);

  useEffect(() => {
    runRef.current = run;
  }, [run]);

  const resolve = useCallback(async () => {
    setPhase('loading');
    try {
      await ensureSessionReady();
      let result = await resolveRunForWeb(shortId);
      if (!result.ok && result.reason === 'auth_required' && await hasVerifiedPhoneOnSession()) {
        result = await resolveRunForWeb(shortId);
      }
      if (result.ok) {
        // A success without a payload would land on the `!run` branch below and
        // spin forever; surface it as retryable instead.
        if (!result.run) {
          setErrorReason('network_error');
          setPhase('error');
          return;
        }
        commitRun(result.run);
        setPhase(result.run.state === 'denied' ? 'done' : 'ready');
        return;
      }
      if (result.reason === 'auth_required') {
        setPhase('phone_verify');
        return;
      }
      if (result.reason === 'settled') {
        setPhase('done');
        return;
      }
      setErrorReason(result.reason);
      setPhase('error');
    } catch (err) {
      setErrorReason(classifyCallableError(err));
      setPhase('error');
    }
  }, [commitRun, shortId]);

  useEffect(() => {
    void resolve();
  }, [resolve]);

  const applyLocally = useCallback((base: WebRun, action: RunAction): RunDoc => {
    const uid = getFirebaseAuth().currentUser?.uid || 'web';
    return applyRunAction(toRunDoc(base), action, {
      actorUserId: uid,
      isOwner: false,
      isExecutor: true,
    });
  }, []);

  const sendAction = useCallback(async (
    next: PendingRunAction,
    target: WebRun,
    rollbackTo: WebRun | null,
  ): Promise<boolean> => {
    sendingRef.current = true;
    setSending(true);
    commitPending(next);
    savePendingAction(shortId, next);
    setSendError(null);
    try {
      await advanceRunStepFromWeb({
        runId: target.runId,
        actionId: next.actionId,
        action: next.action,
      });
      clearPendingAction(shortId);
      commitPending(null);
      return true;
    } catch (err) {
      const failure = classifyRunActionError(err);
      setSendError(failure.message);
      if (failure.permanent) {
        // Keeping an action the server will never take wedges every CTA that
        // guards on `pending`, and leaves the list showing work that was not
        // saved. Drop it and undo the optimistic apply instead.
        clearPendingAction(shortId);
        commitPending(null);
        if (rollbackTo) commitRun(rollbackTo);
      }
      return false;
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }, [commitPending, commitRun, shortId]);

  /** Push the queued action, if any, and fold what the server took into `run`. */
  const flushPending = useCallback(async (): Promise<boolean> => {
    const stored = pendingRef.current;
    if (!stored) return true;
    const target = runRef.current;
    if (!target) return false;
    if (!await sendAction(stored, target, null)) return false;
    try {
      const nextDoc = applyLocally(target, stored.action);
      commitRun(mergeRun(target, nextDoc));
      if (nextDoc.state === 'denied') setPhase('done');
    } catch {
      // Already reflected locally — the reducer refuses to apply it twice.
    }
    return true;
  }, [applyLocally, commitRun, sendAction]);

  const applyAndSend = useCallback(async (action: RunAction): Promise<boolean> => {
    if (runRef.current && runRef.current.canExecute === false) {
      setSendError('Only the assignee can work this run.');
      return false;
    }
    // A second action must never silently overwrite one the server has not
    // taken yet: the single pending slot would drop it without a trace.
    if (pendingRef.current && !await flushPending()) return false;

    const base = runRef.current;
    if (!base) return false;

    let nextDoc: RunDoc;
    try {
      nextDoc = applyLocally(base, action);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'This action is not allowed.');
      return false;
    }
    const optimistic = mergeRun(base, nextDoc);

    // Accept / Decline gate the entire run. Applying them optimistically hides
    // the footer and unlocks the steps before the server has agreed, which is
    // how a refused accept turned into a run the executor could work on but
    // never save (2026-09-09). Everything else stays optimistic.
    const lifecycle = action.type === RUN_ACTIONS.ACCEPT || action.type === RUN_ACTIONS.DENY;
    if (!lifecycle) commitRun(optimistic);

    const sent = await sendAction(
      { actionId: newActionId(), action, createdAt: Date.now() },
      base,
      lifecycle ? null : base,
    );
    if (!sent) return false;

    commitRun(optimistic);
    if (nextDoc.state === 'denied') setPhase('done');
    return true;
  }, [applyLocally, commitRun, flushPending, sendAction]);

  const retryPending = useCallback(async () => {
    if (await flushPending()) setOpenStep(null);
  }, [flushPending]);

  // Replay whatever the last session left unsent. This has to wait for `run`
  // (the send needs a runId) and must fire exactly once, or the guard that
  // restores `pending` starves the send that clears it.
  useEffect(() => {
    if (!run || restoredRef.current) return;
    restoredRef.current = true;
    if (run.canExecute === false) return;
    const stored = loadPendingAction(shortId);
    if (!stored) return;
    commitPending(stored);
    void flushPending();
  }, [commitPending, flushPending, run, shortId]);

  useEffect(() => {
    const uid = getFirebaseAuth().currentUser?.uid;
    const mayListen = !!uid && (run?.executorUserId === uid || run?.ownerUserId === uid);
    if (!run?.runId || !mayListen || liveRef.current) return;
    liveRef.current = true;
    const unsub = onSnapshot(doc(getFirebaseDb(), 'routineRuns', run.runId), (snap) => {
      if (!snap.exists() || sendingRef.current || loadPendingAction(shortId)) return;
      const data = snap.data() as RunDoc;
      setRun((prev) => (prev ? mergeRun(prev, { ...data, runId: snap.id }) : prev));
      if (data.state === 'denied') setPhase('done');
    });
    return () => {
      liveRef.current = false;
      unsub();
    };
  }, [run?.executorUserId, run?.ownerUserId, run?.runId, shortId]);

  const handleVerified = useCallback(async () => {
    try {
      await ensureContactForPhoneAuth();
    } catch {
      // harvest is best-effort; resolve still phone-matches
    }
    setForcePhoneInput(false);
    void resolve();
  }, [resolve]);

  const handleChangePhone = useCallback(async () => {
    setPhase('loading');
    try {
      await signOutOfSession();
    } catch {
      // Still collect a new number — the stale session is what blocked us.
    }
    setForcePhoneInput(true);
    setPhase('phone_verify');
  }, []);

  const withBanner = (node: ReactNode) => (
    <>
      <AppInstallHeadBanner open={installBanner.visible} onDismiss={installBanner.dismiss} />
      {node}
    </>
  );

  if (phase === 'loading') return withBanner(<LoadingStep label="Opening run…" />);
  if (phase === 'phone_verify') {
    return withBanner(
      <PhoneVerifyStep
        forceInput={forcePhoneInput}
        onVerified={() => void handleVerified()}
      />,
    );
  }
  if (phase === 'error') {
    return withBanner(
      <ErrorScreen
        noun="run"
        reason={errorReason}
        onRetry={() => void resolve()}
        onChangePhone={errorReason === 'phone_mismatch' ? () => void handleChangePhone() : undefined}
      />,
    );
  }
  if (phase === 'done') {
    return withBanner(
          <RunDoneStep
            title={run?.bucketName}
            flagged={run?.state === 'flagged'}
            declined={run?.state === 'denied'}
            hasHome={hasHome}
          />,
    );
  }
  if (!run) return withBanner(<LoadingStep label="Opening run…" />);

  const canExecute = run.canExecute !== false;
  const awaitingAccept = !run.acceptedAt && run.state === 'pending';
  const needsAccept = canExecute && awaitingAccept;
  const pendingStepId = pending?.action.stepId || null;
  const liveOpenStep = openStep
    ? run.steps.find((s) => s.stepId === openStep.stepId) || openStep
    : null;
  const runFinished = isRunSettled(toRunDoc(run));

  const requestOutcome = (step: WebRunStep, outcome: 'yes' | 'no') => {
    if (stepNeedsEvidence(step, outcome)) {
      setSendError(null);
      setEvidence({ step, outcome });
      return;
    }
    void applyAndSend(actionForStep(step, { outcome })).then((ok) => {
      if (ok) {
        setOpenStep(null);
        setEvidence(null);
        setEditingNote(false);
      }
    });
  };

  const requestReopen = (step: WebRunStep) => {
    if (!isStepResolved(step)) return;
    void applyAndSend({ type: RUN_ACTIONS.REOPEN_STEP, stepId: step.stepId }).then((ok) => {
      if (ok) setEditingNote(false);
    });
  };

  const evidenceStep = evidence?.step || liveOpenStep;
  const evidenceBusy = !!(
    sending
    && pending
    && evidenceStep
    && pending.action.stepId === evidenceStep.stepId
  );
  // A failed action that is no longer queued has nothing left to retry.
  const onRetry = pending ? () => void retryPending() : null;
  const overlayOpen = !!(liveOpenStep || evidence || editingNote);
  const canGoBack = !!inAppBackTarget();
  const showBack = canGoBack;
  const showHome = hasHome && !canGoBack;

  return withBanner(
    <div className="h-full bg-white text-x9-ink">
      <div className="run-execute-scroll px-4 pb-36">
      <header className="pt-8">
        <div className="mx-auto mb-4 w-[min(17vw,4.2rem)] text-x9-marine">
          <X9Logo title="X9" className="block w-full select-none" />
        </div>
        <p className="text-center text-xs uppercase tracking-wide text-x9-marineSoft">
          {run.ownerDisplay}
        </p>
        <h1 className="text-center text-xl font-semibold text-x9-ink">{run.bucketName}</h1>
        <p className="mb-5 mt-1 text-center text-sm text-x9-marineSoft">
          {run.steps.length} steps
          {!canExecute
            ? awaitingAccept
              ? ' · Waiting for the assignee to accept'
              : ' · Review only — assigned to someone else'
            : needsAccept
              ? ' · Accept to tell the owner you have started'
              : runFinished
                ? ' · All steps resolved · swipe left to reopen'
                : ' · Swipe right Yes · left No · left on a done step to reopen'}
        </p>
      </header>
      <RunListView
        run={run}
        pendingStepId={pendingStepId}
        canOpenSteps={!needsAccept}
        canSwipe={canExecute && !needsAccept}
        onOpenStep={(step) => {
          // Don't carry another step's failure into this sheet.
          setSendError(null);
          setEditingNote(false);
          setOpenStep(step);
        }}
        onSwipeYes={(step) => requestOutcome(step, 'yes')}
        onSwipeNo={(step) => requestOutcome(step, 'no')}
        onSwipeReopen={requestReopen}
      >
        {sendError && !openStep && !evidence && !editingNote ? (
          <div className="mt-4 rounded-xl bg-x9-paper px-4 py-3 text-sm text-x9-ink">
            <p>{sendError}</p>
            {onRetry ? (
              <button type="button" className="mt-2 font-medium text-x9-marine" onClick={onRetry}>
                Retry
              </button>
            ) : null}
          </div>
        ) : null}
      </RunListView>
      </div>

      {/*
        One exit: Back walks the in-app stack; Home is only for a cold /
        shared link. They share this bar with Accept so the two never fight
        for the bottom of the screen.
      */}
      {!overlayOpen && (needsAccept || showHome || showBack) ? (
        <FloatingFooter>
          {showBack ? (
            <FloatingFooterNavButton
              label="Back"
              disabled={sending}
              onClick={() => {
                if (!goBackInApp()) navigateTo(HOME_PATH);
              }}
            />
          ) : null}
          {showHome ? (
            <FloatingFooterNavButton
              label="Home"
              icon={homeOutline}
              disabled={sending}
              onClick={() => navigateTo(HOME_PATH)}
            />
          ) : null}
          {needsAccept ? (
            <>
              <FloatingFooterCta
                tone="muted"
                className="!flex-1"
                disabled={sending}
                onClick={() => void applyAndSend({ type: RUN_ACTIONS.DENY })}
              >
                {sending && pending?.action.type === 'deny' ? 'Declining…' : 'Decline'}
              </FloatingFooterCta>
              <FloatingFooterCta
                className="!flex-[2]"
                disabled={sending}
                onClick={() => void applyAndSend({ type: RUN_ACTIONS.ACCEPT })}
              >
                {sending && pending?.action.type === 'accept' ? 'Accepting…' : 'Accept run'}
              </FloatingFooterCta>
            </>
          ) : null}
        </FloatingFooter>
      ) : null}

      {liveOpenStep && !evidence && !editingNote ? (
        <RunStepSheet
          step={liveOpenStep}
          readOnly={!canExecute}
          sending={sending && pending?.action.stepId === liveOpenStep.stepId}
          sendError={sendError}
          onChoose={(outcome) => requestOutcome(liveOpenStep, outcome)}
          onReopen={() => requestReopen(liveOpenStep)}
          onEditNote={() => setEditingNote(true)}
          onRetry={onRetry}
          onClose={() => {
            setEditingNote(false);
            setOpenStep(null);
          }}
        />
      ) : null}

      {evidence ? (
        <RunEvidenceModal
          shortId={shortId}
          step={evidence.step}
          outcome={evidence.outcome}
          sending={evidenceBusy}
          sendError={sendError}
          onConfirm={(input) => {
            void applyAndSend(actionForStep(evidence.step, {
              outcome: evidence.outcome,
              ...input,
            })).then((ok) => {
              if (ok) {
                setEvidence(null);
                setOpenStep(null);
              }
            });
          }}
          onRetry={onRetry}
          onClose={() => setEvidence(null)}
        />
      ) : null}

      {editingNote && liveOpenStep ? (
        <RunEditNoteModal
          shortId={shortId}
          initialValue={
            liveOpenStep.resolutionMode === 'execute'
              ? (liveOpenStep.note || '')
              : (liveOpenStep.details || liveOpenStep.note || '')
          }
          requireNote={stepNoteRequiredForCurrent(liveOpenStep)}
          sending={sending && pending?.action.type === 'editStepNote'}
          sendError={sendError}
          onConfirm={(note) => {
            void applyAndSend({
              type: RUN_ACTIONS.EDIT_STEP_NOTE,
              stepId: liveOpenStep.stepId,
              note,
            }).then((ok) => {
              if (ok) setEditingNote(false);
            });
          }}
          onRetry={onRetry}
          onClose={() => setEditingNote(false)}
        />
      ) : null}
    </div>,
  );
}
